import React, { useState, useEffect } from "react";
import {
  Box,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
} from "@mui/material";
import { getApiUrl, getApiHeaders } from "../../config/api";
import { auth } from "../../config/firebaseConfig";

const PendingClaims = ({ isTabView }) => {
  const tableHeaders = [
    "Item Number",
    "Item Type",
    "Description",
    "Claimant Information",
    "Appointment Date",
    "Approval Status",
  ];

  const [claimData, setClaimData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;
      
      const token = await user.getIdToken();
      const response = await fetch(getApiUrl("api/claims/all"), {
        headers: getApiHeaders({ Authorization: `Bearer ${token}` })
      });
      
      if (!response.ok) throw new Error("Failed to fetch claims");
      
      const data = await response.json();
      console.log("Fetched claims data:", data);
      
      const formattedData = (data.claims || [])
        .filter(claim => claim.status === 'pending')
        .map(claim => ({
          id: `#${claim.id?.toString().padStart(3, '0') || "000"}`,
          type: claim.item_type || "N/A",
          description: claim.item_description || "No description",
          claimant: claim.full_name || "Unknown",
          appointment: claim.claim_date 
            ? new Date(claim.claim_date).toLocaleDateString('en-US')
            : "N/A",
          status: claim.status || "pending"
        }));
      
      console.log("Formatted pending claims:", formattedData);
      setClaimData(formattedData);
    } catch (error) {
      console.error("Error fetching claims:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (claimId, newStatus) => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      
      const token = await user.getIdToken();
      const response = await fetch(getApiUrl(`api/claims/${claimId.replace('#', '')}/status`), {
        method: "PUT",
        headers: getApiHeaders({ 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }),
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        // Refresh the data to get updated list
        fetchClaims();
      }
    } catch (error) {
      console.error("Error updating claim status:", error);
    }
  };

  if (isTabView) {
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <TableContainer sx={{ flex: 1, bgcolor: "#fbffe8", borderRadius: "15px", border: "2px solid #516d4f" }}>
          <Table>
            <TableHead>
              <TableRow>
                {tableHeaders.map((header, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      p: 2,
                      textAlign: "center",
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 600,
                      fontSize: "1rem",
                      color: "#526D50",
                      borderRight: index < tableHeaders.length - 1 ? "1px solid #d0e7ba" : "none",
                      borderBottom: "2px solid #d0e7ba"
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index} sx={{ height: "80px" }}>
                    <TableCell sx={{ borderRight: "1px solid #d0e7ba", textAlign: "center" }}><Skeleton variant="text" width="60%" sx={{ mx: "auto" }} height={30} /></TableCell>
                    <TableCell sx={{ borderRight: "1px solid #d0e7ba", textAlign: "center" }}><Skeleton variant="text" width="70%" sx={{ mx: "auto" }} height={30} /></TableCell>
                    <TableCell sx={{ borderRight: "1px solid #d0e7ba", textAlign: "center" }}><Skeleton variant="text" width="80%" sx={{ mx: "auto" }} height={30} /></TableCell>
                    <TableCell sx={{ borderRight: "1px solid #d0e7ba", textAlign: "center" }}><Skeleton variant="text" width="70%" sx={{ mx: "auto" }} height={30} /></TableCell>
                    <TableCell sx={{ borderRight: "1px solid #d0e7ba", textAlign: "center" }}><Skeleton variant="text" width="60%" sx={{ mx: "auto" }} height={30} /></TableCell>
                    <TableCell sx={{ textAlign: "center" }}><Skeleton variant="rounded" width="200px" height="40px" sx={{ mx: "auto", borderRadius: "25px" }} /></TableCell>
                  </TableRow>
                ))
              ) : (
                claimData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      borderBottom: index < claimData.length - 1 ? "1px solid #d0e7ba" : "none",
                      height: "80px",
                    }}
                  >
                    <TableCell sx={{ borderRight: "1px solid #d0e7ba", textAlign: "center", color: "#526D50" }}>{row.id}</TableCell>
                    <TableCell sx={{ borderRight: "1px solid #d0e7ba", textAlign: "center", color: "#526D50" }}>{row.type}</TableCell>
                    <TableCell sx={{ borderRight: "1px solid #d0e7ba", textAlign: "center", color: "#526D50" }}>{row.description}</TableCell>
                    <TableCell sx={{ borderRight: "1px solid #d0e7ba", textAlign: "center", color: "#526D50" }}>{row.claimant}</TableCell>
                    <TableCell sx={{ borderRight: "1px solid #d0e7ba", textAlign: "center", color: "#526D50" }}>{row.appointment}</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Select
                        value={row.status}
                        displayEmpty
                        onChange={(e) => handleStatusChange(row.id, e.target.value)}
                        sx={{
                          bgcolor: "#aac093",
                          color: "white",
                          borderRadius: "25px",
                          width: "200px",
                          height: "40px",
                          fontSize: "15px",
                          "& .MuiSelect-select": {
                            py: 1,
                            px: 1.5,
                            fontFamily: "'Poppins-Regular', Helvetica",
                        },
                      }}
                      renderValue={(value) => value || "Select Status"}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
              ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <TableContainer sx={{ flex: 1, bgcolor: "#fbffe8", borderRadius: "15px", border: "2px solid #516d4f" }}>
        <Table>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header, index) => (
                <TableCell
                  key={index}
                  sx={{
                    p: 2,
                    textAlign: "center",
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "#526D50",
                    borderRight: index < tableHeaders.length - 1 ? "1px solid #d0e7ba" : "none",
                    borderBottom: "2px solid #d0e7ba"
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {claimData.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  borderBottom: index < claimData.length - 1 ? "1px solid #d0e7ba" : "none",
                  height: "80px",
                }}
              >
                <TableCell sx={{ borderRight: "1px solid #d0e7ba", textAlign: "center", color: "#526D50" }}>{row.id}</TableCell>
                <TableCell sx={{ borderRight: "1px solid #d0e7ba", textAlign: "center", color: "#526D50" }}>{row.type}</TableCell>
                <TableCell sx={{ borderRight: "1px solid #d0e7ba", textAlign: "center", color: "#526D50" }}>{row.description}</TableCell>
                <TableCell sx={{ borderRight: "1px solid #d0e7ba", textAlign: "center", color: "#526D50" }}>{row.claimant}</TableCell>
                <TableCell sx={{ borderRight: "1px solid #d0e7ba", textAlign: "center", color: "#526D50" }}>{row.appointment}</TableCell>
                <TableCell sx={{ textAlign: "center" }}>
                  <Select
                    value={row.status}
                    displayEmpty
                    onChange={(e) => handleStatusChange(row.id, e.target.value)}
                    sx={{
                      bgcolor: "#aac093",
                      color: "white",
                      borderRadius: "25px",
                      width: "200px",
                      height: "40px",
                      fontSize: "15px",
                      "& .MuiSelect-select": {
                        py: 1,
                        px: 1.5,
                        fontFamily: "'Poppins-Regular', Helvetica",
                      },
                    }}
                    renderValue={(value) => value || "Select Status"}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PendingClaims;